import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CreditCard, Phone, RefreshCw } from 'lucide-react';

/**
 * PaymentDeclineNotice (Phase 178)
 * --------------------------------
 * Shown inside every Stripe Elements form when a card is declined.
 *
 *   decline        — object from explainStripeError()/handleStripeDecline()
 *   onTryAnother   — clears the card form so the parent can enter another card
 *   onPayAnotherWay— optional: switch to the coach's offline / pay-later option
 *   onRetry        — optional: re-submit with the same card (shown when the
 *                    decline is retry-friendly, e.g. typo in CVC)
 */
export function PaymentDeclineNotice({ decline, onTryAnother, onPayAnotherWay, onRetry, idPrefix = 'decline' }) {
    const { t: t42 } = useLocaleR42();
    if (!decline) return null;

    // Form-validation errors (card number incomplete etc.) — keep it light.
    if (decline.isValidationError) {
        return (
            <div className="rounded-xl border border-[#ff6b35]/40 bg-[#fff5f0] p-3 text-sm text-[#ff6b35]" data-testid={`${idPrefix}-validation-msg`}>
                {decline.stripeMessage || 'Please check the card details and try again.'}
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-[#ff6b35]/40 bg-[#fff5f0] p-4 text-sm text-[#0a1628]" role="alert" data-testid={`${idPrefix}-notice`}>
            <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#ff6b35]" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                    <div className="font-semibold" data-testid={`${idPrefix}-title`}>{decline.title}</div>
                    <p className="mt-0.5 text-[#1a3a5c]" data-testid={`${idPrefix}-reason`}>{decline.reason}</p>
                    <p className="mt-1 text-[#1a3a5c]"><strong>{t42("What to do:")}</strong> <span data-testid={`${idPrefix}-suggestion`}>{decline.suggestion}</span></p>
                    <p className="mt-1 text-xs text-[#1a3a5c]/70">{t42("Nothing was charged. Your coach has been told the payment didn’t go through.")}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {onTryAnother && (
                            <Button type="button" onClick={onTryAnother} className="h-10 rounded-xl bg-[#0a1628] text-white hover:bg-[#1a3a5c]" data-testid={`${idPrefix}-try-another-card`}>
                                <CreditCard className="mr-2 h-4 w-4" /> {t42("Try another card")} </Button>
                        )}
                        {decline.retrySame && onRetry && (
                            <Button type="button" variant="outline" onClick={onRetry} className="h-10 rounded-xl" data-testid={`${idPrefix}-retry-same`}>
                                <RefreshCw className="mr-2 h-4 w-4" /> {t42("Try this card again")} </Button>
                        )}
                        {onPayAnotherWay && (
                            <Button type="button" variant="ghost" onClick={onPayAnotherWay} className="h-10 rounded-xl text-[#1a3a5c]" data-testid={`${idPrefix}-pay-another-way`}> {t42("Pay another way")} </Button>
                        )}
                    </div>
                    {!decline.retrySame && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-[#1a3a5c]/70"><Phone className="h-3 w-3" /> {t42("Prefer to keep this card? Call the number on the back and ask them to approve the charge, then try again.")}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PaymentDeclineNotice;
